import { useState } from "react";

import type { CoverageRow, Progress } from "../../services/curriculumService";
import { ClientRegister, StatusDot, type RegisterColumn } from "../register/ClientRegister";
import { DetailsModal } from "../register/DetailsModal";
import { Notice, type NoticeState } from "../register/RecordParts";
import { ClassSyllabus, CompleteForm, UpdateForm } from "./CoverageParts";
import { TOPIC_STATES, coverageConfig, coverageStatus, day, recordConfig, topicState } from "./coverageLogic";

const Bar = ({ value, tone }: { value: number; tone: string }) => (
  <span className="flex items-center gap-2">
    <span className="h-1.5 w-20 overflow-hidden rounded-full bg-gray-100"><span className={`block h-full rounded-full ${tone}`} style={{ width: `${Math.min(100, value)}%` }} /></span>
    <span className="tabular-nums text-gray-700">{value}%</span>
  </span>
);

/** Each class's way through each syllabus it follows. */
export function CoveragePanel({ rows, me, isLoading, isError, onRetry, onChanged, initialView }: {
  rows?: CoverageRow[];
  me?: number;
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
  onChanged: () => void;
  initialView?: string;
}) {
  const [notice, setNotice] = useState<NoticeState>(null);
  const [viewing, setViewing] = useState<string | null>(null);
  const columns: RegisterColumn<CoverageRow>[] = [
    { header: "Class", cell: (row) => <span className="font-medium text-gray-900">{row.class_name}</span>, exportValue: (row) => row.class_name },
    { header: "Subject", cell: (row) => row.subject_name, exportValue: (row) => row.subject_name, className: "text-gray-900" },
    { header: "Syllabus", cell: (row) => row.syllabus_title, exportValue: (row) => row.syllabus_title, exportOnly: true },
    { header: "Topics", cell: (row) => row.topic_count, exportValue: (row) => row.topic_count, align: "right", className: "tabular-nums text-gray-700" },
    { header: "Done", cell: (row) => row.completed, exportValue: (row) => row.completed, align: "right", className: "tabular-nums text-gray-700" },
    { header: "Under way", cell: (row) => row.in_progress, exportValue: (row) => row.in_progress, align: "right", className: "tabular-nums text-gray-700" },
    { header: "Behind", cell: (row) => (row.behind ? <span className="font-semibold text-red-700">{row.behind}</span> : "—"), exportValue: (row) => row.behind, align: "right", className: "tabular-nums" },
    { header: "Covered", cell: (row) => <Bar value={row.coverage} tone={row.behind ? "bg-red-500" : "bg-emerald-500"} />, exportValue: (row) => row.coverage },
    { header: "Status", cell: (row) => <StatusDot {...coverageStatus(row)} />, exportValue: (row) => coverageStatus(row).label },
    { header: "Teachers", cell: (row) => row.teachers.join(", "), exportValue: (row) => row.teachers.join(", "), exportOnly: true },
    { header: "Last update", cell: (row) => day(row.last_updated), exportValue: (row) => day(row.last_updated), exportOnly: true },
  ];
  const row = viewing ? rows?.find((item) => item.id === viewing) : undefined;
  return (
    <div className="space-y-4">
      <Notice notice={notice} onDismiss={() => setNotice(null)} />
      <ClientRegister
        key={initialView}
        title="Coverage by class"
        description="How far each class has got through each syllabus it follows. A topic is behind once it runs past the weeks it was planned for. Open a row to see its topics and record progress."
        rows={rows}
        config={coverageConfig}
        initialState={initialView ? { view: initialView } : undefined}
        columns={columns}
        rowKey={(item) => item.id}
        onOpen={(item) => setViewing(item.id)}
        openLabel={(item) => (item.can_record ? "Record" : "Open")}
        noun={["class syllabus", "class syllabi"]}
        exportName={{ file: "curriculum-coverage", title: "Curriculum Coverage by Class" }}
        isLoading={isLoading}
        isError={isError}
        onRetry={onRetry}
        emptyText="No syllabi match the classes you can see. Syllabi are set up in Syllabi; teachers see the classes they teach."
      />
      {row && (
        <ClassSyllabus
          row={row}
          me={me}
          onClose={() => setViewing(null)}
          onChanged={(text) => {
            setNotice({ tone: "success", text });
            onChanged();
          }}
        />
      )}
    </div>
  );
}

/** Every topic record: who taught what to which class, and how it went. */
export function RecordsPanel({ records, me, isLoading, isError, onRetry, onChanged }: { records?: Progress[]; me?: number; isLoading: boolean; isError: boolean; onRetry: () => void; onChanged: () => void }) {
  const [notice, setNotice] = useState<NoticeState>(null);
  const [viewing, setViewing] = useState<number | null>(null);
  const [updating, setUpdating] = useState<Progress | null>(null);
  const [completing, setCompleting] = useState<Progress | null>(null);
  const done = (text: string) => {
    setNotice({ tone: "success", text });
    onChanged();
  };
  const columns: RegisterColumn<Progress>[] = [
    { header: "Topic", cell: (row) => <span className="font-medium text-gray-900">{row.topic_name}</span>, exportValue: (row) => row.topic_name, className: "min-w-[12rem]" },
    { header: "Subject", cell: (row) => row.subject_name, exportValue: (row) => row.subject_name, className: "text-gray-700" },
    { header: "Class", cell: (row) => row.class_name, exportValue: (row) => row.class_name, className: "text-gray-700" },
    { header: "Teacher", cell: (row) => row.teacher_name, exportValue: (row) => row.teacher_name, className: "text-gray-700" },
    { header: "Started", cell: (row) => day(row.start_date), exportValue: (row) => day(row.start_date), className: "whitespace-nowrap tabular-nums text-gray-700" },
    { header: "Finished", cell: (row) => day(row.completion_date) || "—", exportValue: (row) => day(row.completion_date), className: "whitespace-nowrap tabular-nums text-gray-700" },
    { header: "Covered", cell: (row) => <Bar value={Math.round(Number(row.progress_percentage))} tone={row.is_behind ? "bg-red-500" : row.status === "completed" ? "bg-emerald-500" : "bg-sky-500"} />, exportValue: (row) => Number(row.progress_percentage) },
    { header: "Status", cell: (row) => <StatusDot {...TOPIC_STATES[topicState(row)]} />, exportValue: (row) => TOPIC_STATES[topicState(row)].label },
    { header: "Weeks planned", cell: (row) => row.topic_weeks, exportValue: (row) => row.topic_weeks, exportOnly: true },
    { header: "Note", cell: (row) => row.notes ?? "", exportValue: (row) => row.notes ?? "", exportOnly: true },
    { header: "What went well", cell: (row) => row.achievements ?? "", exportValue: (row) => row.achievements ?? "", exportOnly: true },
    { header: "What got in the way", cell: (row) => row.challenges ?? "", exportValue: (row) => row.challenges ?? "", exportOnly: true },
  ];
  const record = viewing !== null ? records?.find((row) => row.id === viewing) : undefined;
  const mine = record?.teacher === me;
  return (
    <div className="space-y-4">
      <Notice notice={notice} onDismiss={() => setNotice(null)} />
      <ClientRegister
        title="Topic records"
        description="Every topic a teacher has started with a class, and how it went. Teachers see their own; leaders see everyone's."
        rows={records}
        config={recordConfig(me)}
        columns={columns}
        rowKey={(row) => row.id}
        onOpen={(row) => setViewing(row.id)}
        noun={["record", "records"]}
        exportName={{ file: "coverage-records", title: "Curriculum Coverage Records" }}
        isLoading={isLoading}
        isError={isError}
        onRetry={onRetry}
        emptyText="No topics recorded yet. Start one from Coverage by class."
      />
      {record && (
        <DetailsModal
          title={record.topic_name}
          headline={{ label: `${record.subject_name} · ${record.class_name}`, value: `${Math.round(Number(record.progress_percentage))}% covered`, note: `Planned for ${record.topic_weeks} week${record.topic_weeks === 1 ? "" : "s"}` }}
          status={TOPIC_STATES[topicState(record)]}
          facts={[["Teacher", record.teacher_name], ["Started", day(record.start_date)], ["Finished", day(record.completion_date) || undefined], ["Last update", new Date(record.updated_at).toLocaleString()]]}
          sections={[{ title: "Note", body: record.notes }, { title: "What went well", body: record.achievements }, { title: "What got in the way", body: record.challenges, tone: "warning" }]}
          onClose={() => setViewing(null)}
          footer={
            <>
              <button type="button" className="btn btn-outline" onClick={() => setViewing(null)}>Close</button>
              {mine && <button type="button" onClick={() => setUpdating(record)} className="btn btn-outline">Update</button>}
              {mine && record.status !== "completed" && <button type="button" onClick={() => setCompleting(record)} className="btn btn-primary">Mark it done</button>}
            </>
          }
        />
      )}
      {updating && <UpdateForm record={updating} onClose={() => setUpdating(null)} onDone={(saved) => { setUpdating(null); done(`${saved.topic_name}: ${Math.round(Number(saved.progress_percentage))}% covered.`); }} />}
      {completing && <CompleteForm record={completing} onClose={() => setCompleting(null)} onDone={(saved) => { setCompleting(null); done(`${saved.topic_name} is done with ${saved.class_name}.`); }} />}
    </div>
  );
}
